// Adapted from dev-scrips-pm-react src/scheduling/pages/view-appointment-panel-page.tsx
// (PROD-1712 edit-appointment wiring + PROD-1713 deep-view panel shell).
/**
 * ViewAppointmentPanelPage — full-page route entered from the appointment
 * popup's pencil button (PROD-1712) and from the calendar appointment search
 * (PROD-1714b).
 *
 * Route: /scheduling/appointment/:appointmentId?patientId=&providerId=&profileId=&tab=&from=
 *
 * Flutter parity: `view_appointment_screen.dart` — a tabbed shell wrapped
 * around the appointment + patient + billing data preloaded from
 * `/api/v1/Practice/PreLoadAppointmentDetails`, with a right-side editor pane
 * (create-appointment-style).
 *
 * PROD-1712 (edit-appointment wiring) — the DONE part of this file: all the
 * edit building blocks (`CreateAppointmentPanel` mode="edit" + editAppointment,
 * `appointment-to-form-values.ts`, `form-edit-overlay.ts`
 * `buildEditAppointmentBody`, `useUpdateAppointment`) already existed in PA but
 * were orphaned — nothing ever mounted the panel in edit mode. This page is
 * the "preferred" fix from PA_vs_PM_GAP_ANALYSIS.md §2b (the routed page, not
 * the lighter inline-state alternative) — it doubles as the PROD-1713 deep-view
 * shell so the two tickets share one surface instead of two competing edit
 * entry points.
 *
 * PROD-1713 (deep-view panel tabs) — PARTIAL. Tab set vs PM's 6:
 *   - Patient   — DONE, full port (`view-appointment-panel/patient-tab.tsx`)
 *   - Docs      — PARTIAL (`view-appointment-panel/docs-tab.tsx`) — patient-wide
 *                 list only, no encounter-scoping/consent (see that file's header)
 *   - Vitals    — BLOCKED. PA already has its own vitals surface inside the
 *                 encounter module; the scheduling-filed hooks PM's tab would
 *                 use (`use-create-vitals`, `use-update-vitals`,
 *                 `use-patient-vitals-list`, `use-encounter-vitals`) are
 *                 orphaned DUPLICATES of that surface (PA_vs_PM_GAP_ANALYSIS.md
 *                 §2c note) — building this tab risks a second, divergent
 *                 vitals UI. Needs a product decision, not a guess.
 *   - Forms     — BLOCKED. Needs the questionnaire-response wiring layer
 *                 (`use-questionnaire-definition`, `use-save-questionnaire-response`,
 *                 `use-questionnaire-response-for-patient`) + a ~570-line
 *                 editable-questionnaire-form port — genuinely large, not
 *                 attempted this pass.
 *   - Billing   — PROD-3312, a 1:1 mirror of PM's tab (`view-appointment-panel/
 *                 billing-tab.tsx` → embedded `InvoiceDetailPage`). Gated as PM:
 *                 Show_Billing_Tab (view) AND status NOT in {Booked, Proposed,
 *                 Pending, NoShow, Cancelled} (first appears at Arrived) — except
 *                 a DEVICE booking, which has no encounter lifecycle: there the
 *                 unlock is a persisted invoice id.
 *   - Appointments (recurring series) — BLOCKED. PM's tab is 675 lines with
 *                 per-occurrence actions; `use-recurring-appointment-list.ts`
 *                 exists in PA (orphaned) but wiring the full tab is too large
 *                 for this pass — flagged as a follow-up ticket, not fabricated.
 *
 * Per repo convention ("do NOT fake tabs" / DS-FIRST anti-fiction rule) the
 * BLOCKED tabs render NO content — only Patient + Docs have a `TabsContent`.
 * But per review follow-up (2026-07-24), they are NOT omitted from the
 * tab strip either: a practitioner scanning a bare 2-tab strip next to PM's
 * 6-tab strip has no way to tell "not built yet" from "failed to load". They
 * render as disabled tabs in the shared header tabs (`DEFERRED_TABS` below) — visible, inert, honest. The DS
 * `SubTabDef` carries no per-tab title, so the BLOCKED reason lives in `DEFERRED_TABS` and this header.
 *
 * URL state mirrors PA's PatientEditPage (`?tab=`) so back/forward works.
 */

import { useEffect, useMemo, useState } from 'react';
import { setCrumbName } from '@/app/crumb-name';
import {
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom';
import {
  Skeleton,
  SubTabs,
  Tabs,
  TabsContent,
  type SubTabDef,
} from '@scripsteam/scrips-signal-ds';
import { Avatar } from '@scripsteam/scrips-signal-ds';
import { PageHeader } from '@/shared/ui/components/page-header';
import { identityDob } from '@/encounter/lib/patient-identity';
import { usePermission } from '@/auth/permission-gate';
import { useAppointmentDetails } from '@/services/scheduling/use-appointment-details';
import { ViewAppointmentPatientTab } from '@/scheduling/components/view-appointment-panel/patient-tab';
import { ViewAppointmentDocsTab } from '@/scheduling/components/view-appointment-panel/docs-tab';
import { ViewAppointmentVitalsTab } from '@/scheduling/components/view-appointment-panel/vitals-tab';
import { ViewAppointmentFormsTab } from '@/scheduling/components/view-appointment-panel/forms-tab';
import { ViewAppointmentAppointmentsTab } from '@/scheduling/components/view-appointment-panel/appointments-tab';
import { ViewAppointmentBillingTab } from '@/scheduling/components/view-appointment-panel/billing-tab';
import { isDeviceAppointment } from '@/scheduling/lib/device-edit-request';
import { AppointmentStatusManager } from '@/scheduling/components/appointments/appointment-status-manager';
import { CreateAppointmentPanel } from '@/scheduling/components/appointments/create-appointment-panel';
import { appointmentToFormValues } from '@/scheduling/lib/appointment-to-form-values';
import { useAuthStore } from '@/auth/auth-store';
import { findPatientParticipant } from '@/shared/lib/appointment-participants';
// PROD-2743 design sheet §3 (icons): DS names ON only (?ef=frameV2); OFF keeps today's lucide icon, unchanged.

type TabKey = 'patient' | 'docs' | 'billing' | 'appointments' | 'vitals' | 'forms';

interface TabDescriptor {
  key: TabKey;
  label: string;
  visible: boolean;
}

/**
 * PM's tab order (view-appointment-panel-page.tsx:159-164):
 * Patient · Vitals · Forms · Docs · Billing · Appointments.
 *
 * PROD-1817: PA rendered every LIVE tab before every DEFERRED one, which put
 * Docs SECOND instead of fourth. The set was right; the order was not.
 * Appointments is also gated: PM shows it only for a recurring appointment.
 */
const PM_TAB_ORDER = ['patient', 'vitals', 'forms', 'docs', 'billing', 'appointments'] as const;

/**
 * The tabs PM ships that PA doesn't yet (review follow-up, PROD-1713): rendered as VISIBLE-but-disabled tabs rather
 * than omitted, so a practitioner reads "not built yet" instead of mistaking the absence for a data-load failure.
 * Each `reason` is the same BLOCKED rationale from this file's header, never a generic "coming soon".
 */
// Empty since PROD-3312 shipped Billing; the mechanism stays for the next tab PM has and PA doesn't.
const DEFERRED_TABS: { key: string; label: string; reason: string }[] = [];

// PM view-appointment-panel-page.tsx:76-91, verbatim. Statuses with NO Billing/invoice tab. The invoice (and its
// services) only materialise once the appointment reaches the encounter stage, so the tab first appears at "Arrived"
// and onward. Flutter's literal gate (`view_appointment_screen.dart:258-263`) excludes only {Booked, Pending, NoShow}
// and renders an empty container for Cancelled; per Andrew (2026-06-05) Proposed is pre-arrival too and must not show
// the tab, and Cancelled is hidden outright rather than rendering an empty tab.
const TERMINAL_BILLING_STATUSES = new Set([
  'booked',
  'proposed',
  'pending',
  'noshow',
  'cancelled',
]);

export function ViewAppointmentPanelPage() {
  const navigate = useNavigate();
  // Mounted inside `<Route path="scheduling/appointment/:appointmentId">` in
  // router.tsx, so useParams binds the URL segment.
  const { appointmentId = '' } = useParams<{ appointmentId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const patientId = searchParams.get('patientId') ?? '';
  const providerId = searchParams.get('providerId') ?? '';
  const appointmentProfileId = searchParams.get('profileId') ?? '';

  // In-app Back button target: the calendar day/view the user came from,
  // passed as `from` (see calendar-page.tsx `handleEditAppointment` /
  // `AppointmentSearch`). Falls back to the calendar root for entry points
  // that don't set it.
  // "Proceed to complete patient" (the Arrived dialog, from the calendar or from this page) asks for the patient to be
  // completed: `?complete=patient`. Undefined on a normal visit; each ask bumps the number so the form answers again.
  const [completeRequest, setCompleteRequest] = useState<number | undefined>(() =>
    searchParams.get('complete') === 'patient' ? 1 : undefined,
  );
  // `?tab=reschedule` / `?tab=cancel` (the calendar popup's Reschedule) opens the edit rail in that state (the design's
  // X1 / X2 rail modes, PM's reschedule-in-the-panel). Read once, on arrival.
  const [railStartIn] = useState<'reschedule' | 'cancel' | undefined>(() => {
    const t = searchParams.get('tab');
    return t === 'reschedule' || t === 'cancel' ? t : undefined;
  });
  const requestComplete = () => {
    const next = new URLSearchParams(searchParams);
    next.set('complete', 'patient');
    next.delete('tab');
    setSearchParams(next, { replace: true });
    setCompleteRequest((n) => (n ?? 0) + 1);
  };

  // Arriving with `?complete=patient` shows Patient, once: a `?tab=` that came along is dropped on arrival.
  useEffect(() => {
    if (searchParams.get('complete') === 'patient' && searchParams.has('tab')) {
      const next = new URLSearchParams(searchParams);
      next.delete('tab');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const goBack = () => navigate(searchParams.get('from') || '/calendar');

  const user = useAuthStore((s) => s.user);

  const canViewVitals = usePermission(['Manage_Vitals'], { view: true });
  const canViewForms = usePermission(['Manage_Questionnaire'], { view: true });
  const canViewPatient = usePermission(['Manage_Patients_Demographics'], { view: true });
  const canViewPatientContacts = usePermission(['Patient_Contacts'], { view: true });
  const canViewBilling = usePermission(['Show_Billing_Tab'], { view: true });

  // Notification / search deep-link entry points may ship only
  // `appointmentId` (no patientId/providerId/profileId query params). The
  // fetch is gated on all four being present, so without an explicit guard
  // the page would silently render an empty tab shell.
  const hasFullContext =
    !!appointmentId && !!patientId && !!providerId && !!appointmentProfileId;
  const missingContextDeepLink = !!appointmentId && !hasFullContext;

  const { data, isLoading, isError } = useAppointmentDetails({
    appointmentId,
    patientId,
    providerId,
    appointmentProfileId,
    enabled: hasFullContext,
  });

  const appt = data?.appointment;
  const patient =
    data?.patientData?.patient ?? findPatientParticipant(appt?.participant)?.patient;

  // PM's expression (view-appointment-panel-page.tsx:142-144) — the series id
  // arrives either nested on `recurring` or flat as `recurringId`.
  const isRecurring =
    !!appt?.recurring?.id ||
    !!(appt as { recurringId?: string } | undefined)?.recurringId;

  // PM's Billing unlock (view-appointment-panel-page.tsx:169-196). A device booking (a Device participant and NO
  // practitioner, read from the LOADED appointment) has no encounter lifecycle — it is billed on booking/arrival — so
  // its unlock is an invoice with a persisted id. A practitioner appointment unlocks once it is past the early states.
  const isDeviceBooking = isDeviceAppointment(appt);
  const statusCode = (appt?.status?.code ?? '').toLowerCase();
  const hasPersistedInvoice = !!data?.billingData?.invoice?.id;
  const billingTabUnlocked = isDeviceBooking
    ? hasPersistedInvoice
    : !!appt?.status && !TERMINAL_BILLING_STATUSES.has(statusCode);

  const tabs = useMemo<TabDescriptor[]>(
    () => [
      { key: 'patient', label: 'Patient', visible: canViewPatient },
      { key: 'vitals', label: 'Vitals', visible: canViewVitals },
      { key: 'forms', label: 'Forms', visible: canViewForms },
      { key: 'docs', label: 'Docs', visible: true },
      { key: 'billing', label: 'Billing', visible: canViewBilling && billingTabUnlocked },
      // PROD-1817: ported from PM (appointments-tab.tsx, zero missing deps).
      // Flutter has it too (view_appointment_screen.dart:329) and Andrew can
      // reach it in the live app; PA had it disabled behind a "not ported yet"
      // tooltip. PM gates it on isRecurring — same here.
      { key: 'appointments', label: 'Appointments', visible: isRecurring },
    ],
    [canViewPatient, canViewVitals, canViewForms, canViewBilling, billingTabUnlocked, isRecurring],
  );

  // Seed the edit form from the loaded appointment. Memoised on `appt`
  // identity — the panel re-keys its form provider on the hydrated
  // provider/profile/slot anyway, so a stable object avoids needless work.
  const editInitialValues = useMemo(
    () =>
      appt
        ? appointmentToFormValues(
            appt,
            data?.patientData?.patient as Parameters<
              typeof appointmentToFormValues
            >[1],
          )
        : undefined,
    [appt, data?.patientData?.patient],
  );

  // Active tab is driven entirely by the `?tab=` query param — reactive in
  // react-router, so a URL change while mounted (e.g. "Open in tab" from the
  // popup) re-renders with the new requestedTab.
  // The complete-the-patient ask shows Patient once, when asked (`requestComplete` clears `?tab=`); it never pins it.
  const requestedTab = (searchParams.get('tab') ?? 'patient') as TabKey;
  const activeTab: TabKey = tabs.find((t) => t.key === requestedTab && t.visible)
    ? requestedTab
    : 'patient';

  const setActiveTab = (key: TabKey) => {
    const next = new URLSearchParams(searchParams);
    if (key === 'patient') {
      next.delete('tab');
    } else {
      next.set('tab', key);
    }
    setSearchParams(next, { replace: false });
  };

  // The header's tabs are the app's shared header tabs (the DS SubTabs under the `.page-header-tabs` rules), the same as
  // the patient chart's, in PM's order. Live and deferred tabs interleave (Docs sits fourth); a deferred tab is
  // disabled, so it never activates and never has a panel.
  const headerTabs = useMemo<SubTabDef[]>(
    () =>
      PM_TAB_ORDER.flatMap((key): SubTabDef[] => {
        const live = tabs.find((t) => t.key === key);
        if (live) return live.visible ? [{ id: key, label: live.label }] : [];
        const deferred = DEFERRED_TABS.find((t) => t.key === key);
        return deferred ? [{ id: key, label: deferred.label, disabled: true }] : [];
      }),
    [tabs],
  );

  // Backend ships extra fields beyond PatientResponse — cast through.
  const patientLoose = patient as
    | {
        patientMrn?: string;
        mrn?: string;
        middleName?: string;
        phoneNumber?: string;
      }
    | undefined;

  const displayName =
    [patient?.firstName, patientLoose?.middleName, patient?.lastName]
      .filter(Boolean)
      .join(' ') || 'Appointment';
  // The top bar's last crumb names the appointment's patient: "Calendar › name" (as Practice).
  useEffect(() => {
    setCrumbName(patient ? displayName : null);
    return () => setCrumbName(null);
  }, [patient, displayName]);

  const dob = patient?.dateOfBirth ?? patient?.birthDate ?? null;
  const age = patient?.age ?? null;
  const mrn = patientLoose?.patientMrn ?? patientLoose?.mrn ?? null;
  const phone = patientLoose?.phoneNumber ?? null;
  const importance = patient?.importance ?? null;

  const facts = [
    age ? (typeof age === 'number' || /^\d+$/.test(String(age)) ? `${age}y` : String(age)) : null,
    `DOB ${identityDob(dob) ?? 'N/A'}`,
    `MRN ${mrn?.replace(/^MRN\s*/i, '') || 'N/A'}`,
    canViewPatientContacts && phone ? `Phone ${phone}` : null,
    importance?.code ? `Importance ${importance.code}` : null,
  ].filter((f): f is string => !!f);

  return (
    <Tabs
      value={activeTab}
      onValueChange={(v) => setActiveTab(v as TabKey)}
      className="appt-page flex flex-col gap-0 h-full bg-surface-background"
    >
      {/* The app's one page header (same PageHeader and patient composition as the encounter and the patient chart):
          photo, name, one line of facts, and the page's tabs on its right. Back and the trail live in the top bar. */}
      <PageHeader
        leading={
          isLoading ? (
            <Skeleton className="h-12 w-12 rounded-full" />
          ) : (
            <Avatar
              scale="compact"
              tone="patient"
              name={displayName}
              src={patient?.photoUrl ?? undefined}
              size={48}
            />
          )
        }
        title={isLoading ? <Skeleton className="h-5 w-48" /> : displayName}
        meta={!isLoading && facts.length > 0 ? facts.join(' · ') : undefined}
        tabs={
          <SubTabs
            aria-label="Appointment sections"
            tabs={headerTabs}
            value={activeTab}
            onValueChange={(v) => setActiveTab(v as TabKey)}
          />
        }
      />

      {/* Body — tab area on the left, edit rail on the right */}
      {missingContextDeepLink ? (
        <div className="flex-1 flex flex-col items-center justify-center gap-3 px-6">
          <p className="text-sm text-text-primary">
            This appointment can't be opened directly. Please open it from the calendar.
          </p>
          <button
            type="button"
            onClick={goBack}
            className="text-sm text-brand-primary hover:underline"
          >
            Back to Calendar
          </button>
        </div>
      ) : isError ? (
        <div className="flex-1 flex items-center justify-center text-sm text-status-error">
          Couldn't load appointment.
        </div>
      ) : (
        <div className="flex-1 min-h-0 flex">
          <div className="flex-1 min-h-0 min-w-0 flex flex-col">
            <TabsContent value="patient" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentPatientTab patientId={patientId || null} isLoading={isLoading} completeRequest={completeRequest} />
            </TabsContent>
            <TabsContent value="vitals" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentVitalsTab data={data ?? null} />
            </TabsContent>
            <TabsContent value="forms" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentFormsTab appointment={appt ?? null} />
            </TabsContent>
            <TabsContent value="appointments" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentAppointmentsTab appointment={appt ?? null} />
            </TabsContent>
            <TabsContent value="docs" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentDocsTab appointment={appt ?? null} />
            </TabsContent>
            <TabsContent value="billing" className="flex-1 overflow-auto scrollbar-hide">
              <ViewAppointmentBillingTab data={data ?? null} />
            </TabsContent>
          </div>

          {/* Right pane — editable appointment form (PROD-1712), on the right on every tab (Ahmed, 23 Oct; PM keeps it
              on the left). Renders only once `appt` is loaded so the form seeds with real values. */}
          {appt ? (
            <CreateAppointmentPanel
              open
              mode="edit"
              editAppointment={appt}
              hideFullView
              initialValues={editInitialValues}
              organizationId={appt.organizationId ?? user?.organizationId ?? ''}
              practiceId={appt.practiceId ?? user?.practiceId ?? ''}
              userId={user?.id ?? ''}
              onClose={goBack}
              onCompletePatient={requestComplete}
              startIn={railStartIn}
              onSearchPatients={noopSearchPatients}
            />
          ) : (
            <div className="w-95 flex-shrink-0 border-l border-border-default bg-surface-card" />
          )}
        </div>
      )}

      {/* Footer — status transitions (Mark Arrived / Cancel / Reschedule /
          etc.) via the shared AppointmentStatusManager. Hides when there's
          no appointment yet (initial load + error). */}
      {appt ? (
        <footer className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 px-6 py-3 border-t border-border-default bg-surface-card">
          <AppointmentStatusManager appt={appt} variant="popup" onCompletePatient={requestComplete} />
        </footer>
      ) : null}
    </Tabs>
  );
}

/** Patient is fixed in edit mode (provider/patient locked), so the
 *  panel's patient-search is unused here. */
const noopSearchPatients = async () => [];

export default ViewAppointmentPanelPage;
